/**
 * Onboarding CTAs Component - First Actions Guidance
 * 
 * Shows visual CTAs for first-time actions:
 * - First reservation
 * - First client
 * - First expense
 * 
 * Displayed on dashboard for new riads until actions are completed.
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  PlusCircle, Users, Receipt, ArrowRight, CheckCircle2,
  Sparkles, Calendar, FileText, X
} from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Progress } from '../../components/ui/progress';

const OnboardingCTAs = ({ stats, onDismiss }) => {
  const navigate = useNavigate();
  
  // Define onboarding tasks with completion status
  const tasks = [
    {
      id: 'reservation',
      title: 'Créer votre première réservation',
      description: 'Enregistrez un client pour tester le système',
      icon: Calendar,
      color: 'green',
      completed: (stats?.reservations || 0) > 0,
      action: () => navigate('/reservations?action=new'),
      cta: 'Nouvelle réservation'
    },
    {
      id: 'guest',
      title: 'Ajouter votre premier client',
      description: 'Créez une fiche client dans votre base',
      icon: Users,
      color: 'blue',
      completed: (stats?.guests || 0) > 0,
      action: () => navigate('/guests?action=new'),
      cta: 'Nouveau client'
    },
    {
      id: 'expense',
      title: 'Enregistrer une première dépense',
      description: 'Suivez vos dépenses pour un meilleur suivi',
      icon: Receipt,
      color: 'amber',
      completed: (stats?.expenses || 0) > 0,
      action: () => navigate('/expenses?action=new'),
      cta: 'Nouvelle dépense'
    }
  ];

  const completedCount = tasks.filter(t => t.completed).length;
  const progress = Math.round((completedCount / tasks.length) * 100);

  // Don't show if all tasks are completed
  if (completedCount === tasks.length) {
    return null;
  }

  const colorClasses = {
    green: { bg: 'bg-green-50', text: 'text-green-600', border: 'border-green-200', button: 'bg-green-600 hover:bg-green-700' },
    blue: { bg: 'bg-blue-50', text: 'text-blue-600', border: 'border-blue-200', button: 'bg-blue-600 hover:bg-blue-700' },
    amber: { bg: 'bg-amber-50', text: 'text-amber-600', border: 'border-amber-200', button: 'bg-amber-600 hover:bg-amber-700' }
  };

  return (
    <Card className="bg-gradient-to-r from-violet-50 to-purple-50 border-violet-200" data-testid="onboarding-ctas">
      <CardContent className="p-6">
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-violet-100 flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-violet-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">Premiers pas</h3>
              <p className="text-sm text-gray-500">Complétez ces actions pour bien démarrer</p>
            </div>
          </div>
          {onDismiss && (
            <Button variant="ghost" size="sm" onClick={onDismiss} className="text-gray-400">
              <X className="w-4 h-4" />
            </Button>
          )}
        </div>

        {/* Progress */}
        <div className="mb-6">
          <div className="flex items-center justify-between text-sm mb-2">
            <span className="text-gray-500">{completedCount}/{tasks.length} complétées</span>
            <span className="font-medium text-violet-600">{progress}%</span>
          </div>
          <Progress value={progress} className="h-2" />
        </div>

        {/* Tasks */}
        <div className="space-y-3">
          {tasks.map((task) => {
            const colors = colorClasses[task.color];
            
            return (
              <div 
                key={task.id}
                className={`flex items-center gap-4 p-4 rounded-xl border ${task.completed ? 'bg-gray-50 border-gray-200' : `${colors.bg} ${colors.border}`}`}
                data-testid={`onboarding-task-${task.id}`}
              >
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${task.completed ? 'bg-green-100' : colors.bg}`}>
                  {task.completed ? (
                    <CheckCircle2 className="w-5 h-5 text-green-600" />
                  ) : (
                    <task.icon className={`w-5 h-5 ${colors.text}`} />
                  )}
                </div>
                
                <div className="flex-1 min-w-0">
                  <p className={`font-medium ${task.completed ? 'text-gray-400 line-through' : 'text-gray-900'}`}>
                    {task.title}
                  </p>
                  {!task.completed && (
                    <p className="text-sm text-gray-500">{task.description}</p>
                  )}
                </div>
                
                {!task.completed && (
                  <Button 
                    size="sm" 
                    className={colors.button}
                    onClick={task.action}
                  >
                    {task.cta}
                    <ArrowRight className="w-4 h-4 ml-1" />
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

export default OnboardingCTAs;
